import { randomUUID } from "node:crypto";
import { EventEmitter } from "node:events";

export type TrackResult = {
  requestId: string;
  durationMs: number;
  promptTokens: number;
  completionTokens: number;
  estimatedCostUSD: number;
  requestPayload: unknown;
  responsePayload: unknown;
};

export type TrackerStorage = "memory" | "sqlite";

export type TrackerOptions = {
  storage?: TrackerStorage;
  sqlitePath?: string;
};

function estimateTokens(text: string): number {
  return Math.max(1, Math.ceil(text.length / 4));
}

function extractUsage(response: unknown): { prompt: number; completion: number } | null {
  if (!response || typeof response !== "object") return null;
  const r = response as Record<string, unknown>;
  const usage = (r.usage ?? (r as { data?: { usage?: unknown } }).data) as
    | Record<string, number>
    | undefined;
  if (!usage) return null;
  const prompt = usage.prompt_tokens ?? usage.promptTokens ?? usage.input_tokens;
  const completion =
    usage.completion_tokens ?? usage.completionTokens ?? usage.output_tokens;
  if (typeof prompt === "number" && typeof completion === "number") {
    return { prompt, completion };
  }
  return null;
}

export interface Tracker {
  logExchange(request: unknown, response: unknown): Promise<TrackResult>;
  wrap<T extends (...args: unknown[]) => Promise<unknown>>(fn: T): T;
  on(event: "exchange", handler: (data: TrackResult) => void): void;
}

export function createTracker(_options: TrackerOptions = {}): Tracker {
  const emitter = new EventEmitter();
  const memoryLogs: TrackResult[] = [];

  const logExchange = async (request: unknown, response: unknown): Promise<TrackResult> => {
    const start = Date.now();
    const usage = extractUsage(response);
    const reqText = JSON.stringify(request ?? "");
    const promptTokens = usage?.prompt ?? estimateTokens(reqText);
    const completionTokens =
      usage?.completion ?? estimateTokens(JSON.stringify(response ?? ""));

    const result: TrackResult = {
      requestId: randomUUID(),
      durationMs: Date.now() - start,
      promptTokens,
      completionTokens,
      estimatedCostUSD: 0,
      requestPayload: request,
      responsePayload: response,
    };

    memoryLogs.push(result);
    emitter.emit("exchange", result);
    return result;
  };

  return {
    logExchange,
    wrap<T extends (...args: unknown[]) => Promise<unknown>>(fn: T): T {
      const wrapped = (async (...args: unknown[]) => {
        const request = args[0];
        const response = await fn(...args);
        await logExchange(request, response);
        return response;
      }) as T;
      return wrapped;
    },
    on(event: "exchange", handler: (data: TrackResult) => void) {
      emitter.on(event, handler);
    },
  };
}
